import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Rocket, Loader2, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";

export default function Campaigns() {
  const qc = useQueryClient();
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => api.get("/brands").then(r => r.data) });
  const { data: campaigns = [] } = useQuery({ queryKey: ["campaigns"], queryFn: () => api.get("/campaigns").then(r => r.data) });

  const [brandOpen, setBrandOpen] = useState(false);
  const [campOpen, setCampOpen] = useState(false);
  const [brandForm, setBrandForm] = useState({ name: "", niche: "", voice: "confident and playful", description: "", forbidden_topics: "" });
  const [campForm, setCampForm] = useState({ brand_id: "", name: "", objective: "", kpi: "reach", channels: ["tiktok", "instagram"], budget_credits: 500 });
  const [busy, setBusy] = useState(false);

  const createBrand = async () => {
    setBusy(true);
    try {
      await api.post("/brands", {
        ...brandForm,
        forbidden_topics: brandForm.forbidden_topics.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Brand created");
      setBrandOpen(false);
      setBrandForm({ name: "", niche: "", voice: "confident and playful", description: "", forbidden_topics: "" });
      qc.invalidateQueries({ queryKey: ["brands"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const createCampaign = async () => {
    setBusy(true);
    try {
      await api.post("/campaigns", campForm);
      toast.success("Campaign launched");
      setCampOpen(false);
      setCampForm({ brand_id: "", name: "", objective: "", kpi: "reach", channels: ["tiktok", "instagram"], budget_credits: 500 });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-[#141414] px-8 py-5 sticky top-0 bg-[#050505]/90 backdrop-blur-md z-20 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">// Brands & Campaigns</div>
          <h1 className="font-display font-bold text-2xl">{brands.length} brands · {campaigns.length} campaigns</h1>
        </div>
        <div className="flex gap-2">
          <Dialog open={brandOpen} onOpenChange={setBrandOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-brand-btn" variant="outline" className="h-10 border-[#222] bg-transparent hover:bg-[#141414] hover:text-white rounded-sm">
                <Building2 className="w-4 h-4 mr-2" /> New brand
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#222] rounded-sm max-w-md text-white">
              <DialogHeader><DialogTitle className="font-display font-bold text-xl">Register a brand</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {[["name", "Brand name", "Aurora Records"], ["niche", "Niche", "electronic music label"], ["voice", "Voice", "confident and playful"], ["forbidden_topics", "Forbidden topics (comma sep.)", "politics, gambling"]].map(([k, l, ph]) => (
                  <div key={k}>
                    <Label className="text-xs uppercase font-mono tracking-widest text-[#52525B]">{l}</Label>
                    <Input data-testid={`brand-${k}`} value={brandForm[k]} onChange={(e) => setBrandForm({ ...brandForm, [k]: e.target.value })} placeholder={ph} className="mt-1 h-10 bg-[#0d0d0d] border-[#222] focus:border-[#F5A524] focus-visible:ring-0 rounded-sm" />
                  </div>
                ))}
                <div>
                  <Label className="text-xs uppercase font-mono tracking-widest text-[#52525B]">Description</Label>
                  <Textarea data-testid="brand-description" value={brandForm.description} onChange={(e) => setBrandForm({ ...brandForm, description: e.target.value })} rows={2} className="mt-1 bg-[#0d0d0d] border-[#222] focus:border-[#F5A524] focus-visible:ring-0 rounded-sm" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="brand-submit" disabled={busy || !brandForm.name} onClick={createBrand} className="bg-[#F5A524] hover:bg-[#D98218] text-black font-medium rounded-sm">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Register brand
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={campOpen} onOpenChange={setCampOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-campaign-btn" className="h-10 bg-[#F5A524] hover:bg-[#D98218] text-black font-medium rounded-sm">
                <Plus className="w-4 h-4 mr-2" /> New campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#222] rounded-sm max-w-md text-white">
              <DialogHeader><DialogTitle className="font-display font-bold text-xl">Launch a campaign</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase font-mono tracking-widest text-[#52525B]">Brand</Label>
                  <Select value={campForm.brand_id} onValueChange={(v) => setCampForm({ ...campForm, brand_id: v })}>
                    <SelectTrigger data-testid="campaign-brand" className="mt-1 h-10 bg-[#0d0d0d] border-[#222] rounded-sm"><SelectValue placeholder="Select brand" /></SelectTrigger>
                    <SelectContent className="bg-[#101010] border-[#222] rounded-sm">
                      {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {[["name", "Campaign name"], ["objective", "Objective"], ["kpi", "Primary KPI"]].map(([k, l]) => (
                  <div key={k}>
                    <Label className="text-xs uppercase font-mono tracking-widest text-[#52525B]">{l}</Label>
                    <Input data-testid={`campaign-${k}`} value={campForm[k]} onChange={(e) => setCampForm({ ...campForm, [k]: e.target.value })} className="mt-1 h-10 bg-[#0d0d0d] border-[#222] focus:border-[#F5A524] focus-visible:ring-0 rounded-sm" />
                  </div>
                ))}
                <div>
                  <Label className="text-xs uppercase font-mono tracking-widest text-[#52525B]">Budget (credits)</Label>
                  <Input data-testid="campaign-budget" type="number" value={campForm.budget_credits} onChange={(e) => setCampForm({ ...campForm, budget_credits: parseInt(e.target.value) || 500 })} className="mt-1 h-10 bg-[#0d0d0d] border-[#222] focus:border-[#F5A524] focus-visible:ring-0 rounded-sm" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="campaign-submit" disabled={busy || !campForm.brand_id || !campForm.name} onClick={createCampaign} className="bg-[#F5A524] hover:bg-[#D98218] text-black font-medium rounded-sm">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Launch
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="p-8 space-y-8">
        <section>
          <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-[#52525B]">// Brands</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#171717] border border-[#171717]">
            {brands.map(b => (
              <div key={b.id} className="bg-[#0a0a0a] p-6 hover:bg-[#0f0f0f] transition-colors">
                <div className="w-10 h-10 bg-[#F5A524] rounded-sm flex items-center justify-center mb-3">
                  <span className="font-display font-black text-black text-lg">{b.name.charAt(0)}</span>
                </div>
                <div className="font-display font-bold text-lg">{b.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B] mt-1">{b.niche}</div>
                <p className="text-xs text-[#A1A1AA] mt-3 line-clamp-2">{b.description || "—"}</p>
                <div className="mt-4 pt-3 border-t border-[#141414] flex flex-wrap gap-1">
                  {b.target_platforms?.slice(0, 4).map(p => (
                    <span key={p} className="font-mono text-[9px] uppercase text-[#00F0FF] border border-[#00F0FF]/20 px-1.5 py-0.5">{p}</span>
                  ))}
                </div>
              </div>
            ))}
            {brands.length === 0 && (
              <div className="col-span-full bg-[#0a0a0a] p-16 text-center border border-dashed border-[#222]">
                <Building2 className="w-8 h-8 text-[#52525B] mx-auto mb-3" />
                <div className="font-display font-bold text-lg mb-1">No brands yet.</div>
                <div className="text-sm text-[#A1A1AA]">Register your first brand to start planning campaigns.</div>
              </div>
            )}
          </div>
        </section>

        <section>
          <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-[#52525B]">// Campaigns</div>
          <div className="border border-[#171717] bg-[#0a0a0a]">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#171717] font-mono text-[10px] uppercase tracking-widest text-[#52525B]">
              <div className="col-span-4">Campaign</div>
              <div className="col-span-3">Brand</div>
              <div className="col-span-2">Objective</div>
              <div className="col-span-1">KPI</div>
              <div className="col-span-1 text-right">Budget</div>
              <div className="col-span-1 text-right">Status</div>
            </div>
            {campaigns.map(c => {
              const brand = brands.find(b => b.id === c.brand_id);
              return (
                <div key={c.id} className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#141414] hover:bg-[#0f0f0f] transition-colors text-sm">
                  <div className="col-span-4 flex items-center gap-2"><Rocket className="w-3.5 h-3.5 text-[#F5A524]" /> {c.name}</div>
                  <div className="col-span-3 text-[#A1A1AA]">{brand?.name || "—"}</div>
                  <div className="col-span-2 text-[#A1A1AA] truncate">{c.objective}</div>
                  <div className="col-span-1 font-mono text-xs uppercase">{c.kpi}</div>
                  <div className="col-span-1 text-right font-mono text-xs">{c.budget_credits}</div>
                  <div className="col-span-1 text-right"><span className="font-mono text-[9px] uppercase tracking-widest text-[#17C964]">{c.status}</span></div>
                </div>
              );
            })}
            {campaigns.length === 0 && (
              <div className="p-10 text-center text-sm text-[#52525B]">No campaigns yet.</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
